import { Navigate } from 'react-router-dom';
import { Can } from 'context';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { getCostReport } from 'services/cost-management';
import MDTypography from 'components/MDTypography';
import { Card, Grid, Skeleton, Alert } from '@mui/material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'components/DashboardNavbar';
import Footer from 'examples/Footer';
import UnifiedFilterBar from 'components/UnifiedFilterBar';
import usePersistedFilters from 'hooks/usePersistedFilters';
import { formatDuration } from 'helpers/format-duration';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

export default function CostManagement() {
  // Shared filters (date, entity) - same as sessions/metrics pages
  const { filters: sharedFilters, setDateRange, setEntityFilters } = usePersistedFilters();

  // Local state
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Combined filters for API calls
  const combinedFilters = useMemo(
    () => ({
      startDate: sharedFilters.startDate ? sharedFilters.startDate.toISOString() : null,
      endDate: sharedFilters.endDate ? sharedFilters.endDate.toISOString() : null,
      clientId: sharedFilters.clientIds?.[0] || '',
      operatorId: sharedFilters.operatorIds?.[0] || '',
      brandId: sharedFilters.brandIds?.[0] || ''
    }),
    [sharedFilters]
  );

  // Fetch data when filters change
  useEffect(() => {
    let isActive = true;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await getCostReport(combinedFilters);
        if (isActive) {
          setData(response?.data || []);
        }
      } catch (e) {
        console.error('Failed to fetch cost report', e);
        if (isActive) {
          setError('Failed to load cost report. Please try again.');
          setData([]);
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isActive = false;
    };
  }, [combinedFilters]);

  // Calculate totals for summary card
  const totals = useMemo(() => {
    const totalDuration = data.reduce((sum, item) => sum + (item?.totalDuration || 0), 0);
    const gameCount = data.length;
    return { totalDuration, gameCount };
  }, [data]);

  // Helper to escape CSV values (handles commas, quotes, newlines)
  const escapeCSV = useCallback((value) => {
    if (value === null || value === undefined) return '';
    const str = String(value);
    // If contains comma, quote, or newline, wrap in quotes and escape internal quotes
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  }, []);

  // Export handler
  const handleExport = useCallback(() => {
    if (data.length === 0) return;

    // Create CSV content with proper escaping
    const headers = ['Game Name', 'Game ID', 'Total Playtime (ms)', 'Total Playtime (formatted)'];
    const rows = data.map((item) => [
      escapeCSV(item.gameName),
      escapeCSV(item.gameId),
      escapeCSV(item.totalDuration || 0),
      escapeCSV(formatDuration((item.totalDuration || 0) / 1000))
    ]);

    // Add totals row
    const totalDuration = data.reduce((sum, item) => sum + (item?.totalDuration || 0), 0);
    rows.push([
      escapeCSV('TOTAL'),
      escapeCSV(data.length + ' games'),
      escapeCSV(totalDuration),
      escapeCSV(formatDuration(totalDuration / 1000))
    ]);

    const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    const dateStr = new Date().toISOString().split('T')[0];
    link.setAttribute('download', `cost_report_${dateStr}.csv`);

    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }, [data, escapeCSV]);

  // Date range label for display
  const dateRangeLabel = useMemo(() => {
    if (!sharedFilters.startDate && !sharedFilters.endDate) {
      return 'All Time';
    }
    const start = sharedFilters.startDate ? new Date(sharedFilters.startDate).toLocaleDateString() : 'Beginning';
    const end = sharedFilters.endDate ? new Date(sharedFilters.endDate).toLocaleDateString() : 'Now';
    return `${start} - ${end}`;
  }, [sharedFilters.startDate, sharedFilters.endDate]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Can I='read' a='cost-report'>
        <MDBox pt={6} pb={3}>
          {/* Unified Filter Bar */}
          <UnifiedFilterBar
            filters={sharedFilters}
            setDateRange={setDateRange}
            setEntityFilters={setEntityFilters}
            pageFilterConfig={[]}
          />

          {/* Error State */}
          {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Loading State */}
          {loading ? (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Skeleton variant='rounded' height={120} sx={{ borderRadius: 2 }} />
              </Grid>
              {[1, 2, 3].map((i) => (
                <Grid item xs={12} md={6} lg={4} key={i}>
                  <Skeleton variant='rounded' height={180} sx={{ borderRadius: 2 }} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <>
              {/* Summary Card */}
              <Card sx={{ mb: 3 }}>
                <MDBox p={3}>
                  <MDBox display='flex' justifyContent='space-between' alignItems='center' mb={2}>
                    <MDTypography variant='h5' fontWeight='medium'>
                      Playtime Cost Report
                    </MDTypography>
                    <MDButton
                      variant='gradient'
                      color='info'
                      size='small'
                      onClick={handleExport}
                      disabled={data.length === 0}
                      startIcon={<FileDownloadIcon />}
                    >
                      Export CSV
                    </MDButton>
                  </MDBox>

                  <MDTypography variant='body2' color='text' mb={3}>
                    {dateRangeLabel}
                  </MDTypography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <MDBox display='flex' alignItems='center' p={2} sx={{ bgcolor: 'grey.100', borderRadius: 2 }}>
                        <MDBox
                          display='flex'
                          alignItems='center'
                          justifyContent='center'
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            bgcolor: 'info.main',
                            color: 'white',
                            mr: 2
                          }}
                        >
                          <SportsEsportsIcon />
                        </MDBox>
                        <MDBox>
                          <MDTypography variant='caption' color='text' fontWeight='medium'>
                            Total Games
                          </MDTypography>
                          <MDTypography variant='h4' fontWeight='bold'>
                            {totals.gameCount}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDBox display='flex' alignItems='center' p={2} sx={{ bgcolor: 'grey.100', borderRadius: 2 }}>
                        <MDBox
                          display='flex'
                          alignItems='center'
                          justifyContent='center'
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            bgcolor: 'success.main',
                            color: 'white',
                            mr: 2
                          }}
                        >
                          <AccessTimeIcon />
                        </MDBox>
                        <MDBox>
                          <MDTypography variant='caption' color='text' fontWeight='medium'>
                            Total Playtime
                          </MDTypography>
                          <MDTypography variant='h4' fontWeight='bold'>
                            {formatDuration((totals.totalDuration || 0) / 1000)}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </Grid>
                  </Grid>
                </MDBox>
              </Card>

              {/* Empty State */}
              {data.length === 0 && !error && (
                <Card>
                  <MDBox p={6} textAlign='center'>
                    <SportsEsportsIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                    <MDTypography variant='h6' color='text'>
                      No playtime data found
                    </MDTypography>
                    <MDTypography variant='body2' color='text'>
                      Try adjusting your filters or date range
                    </MDTypography>
                  </MDBox>
                </Card>
              )}

              {/* Game Cards Grid */}
              {data.length > 0 && (
                <Grid container spacing={3}>
                  {data.map((item) => (
                    <Grid item xs={12} md={6} lg={4} key={item?.gameId}>
                      <Card sx={{ height: '100%' }}>
                        <MDBox p={3}>
                          <MDBox display='flex' justifyContent='space-between' alignItems='flex-start' mb={2}>
                            <MDBox>
                              <MDTypography variant='h6' fontWeight='bold' textTransform='capitalize'>
                                {item?.gameName}
                              </MDTypography>
                              <MDTypography variant='caption' color='text'>
                                ID: #{item?.gameId}
                              </MDTypography>
                            </MDBox>
                            <MDBox
                              display='flex'
                              alignItems='center'
                              justifyContent='center'
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                bgcolor: 'info.main',
                                color: 'white'
                              }}
                            >
                              <SportsEsportsIcon fontSize='small' />
                            </MDBox>
                          </MDBox>

                          <MDBox
                            mt={2}
                            p={2}
                            sx={{
                              bgcolor: 'grey.100',
                              borderRadius: 2,
                              borderLeft: '4px solid',
                              borderColor: 'success.main'
                            }}
                          >
                            <MDTypography variant='caption' color='text' fontWeight='medium'>
                              Total Playtime
                            </MDTypography>
                            <MDTypography variant='h5' fontWeight='bold' color='success'>
                              {formatDuration((item?.totalDuration || 0) / 1000)}
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </>
          )}
        </MDBox>
        <Footer />
      </Can>

      <Can not I='read' a='cost-report'>
        <Navigate to='/dashboard' replace />
      </Can>
    </DashboardLayout>
  );
}
